// ErrorBoundary — render-time error catch with friendly fallback + telemetry.
//
// Decision (Session 19AT · S2):
//   Three usage levels:
//     1. <AppErrorBoundary> — wraps entire SPA. Catches anything that escapes
//        a route boundary. Fallback: full-page friendly screen with "刷新" +
//        "回到 home".
//     2. <RouteErrorBoundary> — wraps each <Route>'s element. Catches view-
//        specific render errors so other routes stay intact.
//     3. <SectionErrorBoundary> — sub-tree boundary (e.g. around a flaky
//        markdown render) with a compact inline fallback.
//
// All three:
//   - Auto-record event to operator dashboard (recordEvent type='render-error')
//   - Log to console for dev visibility
//   - Render fallback UI (no white-screen-of-death)
//   - Provide reset() to attempt re-render after user dismisses
//
// Why not react-error-boundary lib: 50 lines hand-rolled fits the no-bloat
// stance; recordEvent integration is custom anyway.

import { Component, type ReactNode, type ErrorInfo } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, RotateCcw, Home as HomeIcon } from 'lucide-react';
import { recordEvent } from '../storage/idb.js';
import { generateErrorId, formatErrorRef } from '../lib/error-id.js';
import { truncateErrorDiagnosis } from '@examforge/shared/api';
import type { ErrorDiagnosis } from '@examforge/shared/api';
import { useT } from '../i18n/index.js';

interface BoundaryProps {
  children: ReactNode;
  /** Custom fallback render. Receives error + reset(). */
  fallback?: (error: Error, reset: () => void) => ReactNode;
  /** Tag for telemetry (e.g. 'app-root' / 'route:quiz' / 'section:markdown'). */
  scope: string;
  /** Reset boundary state when this key changes (e.g. route path). */
  resetKey?: string;
}

interface BoundaryState {
  error: Error | null;
  /**
   * 19BL Session 2: the error handle stamped when the error was caught. Surfaced
   * on the fallback UI so the user can quote `错误号 err_xxx` to the operator.
   * Null when no error is active. Cleared on reset / resetKey change.
   */
  errorId: string | null;
}

class BaseBoundary extends Component<BoundaryProps, BoundaryState> {
  constructor(props: BoundaryProps) {
    super(props);
    this.state = { error: null, errorId: null };
  }

  static getDerivedStateFromError(error: Error): BoundaryState {
    // errorId is minted in componentDidCatch (which runs after this) so the
    // SAME id is used for telemetry + UI. getDerivedStateFromError only flips
    // the error flag to trigger the fallback render; errorId fills in next.
    return { error, errorId: null };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // 19BM-C: console.error guarded — the boundary already reports via
    // recordEvent (render-error → D1 feed + agent waterline). Dev log kept
    // for stack inspection, silenced in prod per CLAUDE.md.
    // eslint-disable-next-line no-console -- DEV-only diagnostic, stripped by Vite in prod build
    if (import.meta.env.DEV) console.error(`[ErrorBoundary:${this.props.scope}]`, error, info);
    // 19BL: mint the handle here so it's identical in telemetry (recordEvent
    // stamps it into payload + D1 PK) and on the fallback UI. Set it on state
    // so the already-triggered fallback re-renders with the handle visible.
    const errorId = generateErrorId();
    this.setState({ errorId });

    // G3 fix (2026-07-01): build an ErrorDiagnosis, then truncateErrorDiagnosis
    // BEFORE recordEvent so the serialized payload stays under D1's payload_json
    // budget (ERROR_DIAGNOSIS_MAX_JSON_CHARS=2000). Before this, the raw
    // stack(4000)+componentStack(4000) payload exceeded the budget and got
    // sliced mid-JSON on the server (recorder.ts), making loadErrorFeed's
    // JSON.parse throw — errorId/code were dropped, breaking the 19BL join.
    // truncateErrorDiagnosis keeps errorId/code/etc. (small, high-signal) and
    // only progressively trims componentStack → stack → recentNav.
    const viewPath = typeof window !== 'undefined' ? window.location.pathname : undefined;
    const diagnosis: ErrorDiagnosis = {
      errorId,
      occurredAt: Date.now(),
      viewPath,
      ...(typeof __APP_VERSION__ !== 'undefined' ? { appVersion: __APP_VERSION__ } : {}),
      stack: error.stack ?? undefined,
      componentStack: info.componentStack ?? undefined,
    };
    truncateErrorDiagnosis(diagnosis);

    void recordEvent({
      type: 'render-error',
      summary: `${this.props.scope}: ${error.message?.slice(0, 180) ?? 'unknown'}`,
      viewPath,
      payload: {
        scope: this.props.scope,
        ...diagnosis,
      },
    });
  }

  override componentDidUpdate(prev: BoundaryProps): void {
    // Reset error when resetKey changes — e.g. route navigation should clear
    // a stale boundary error so the new route gets a fresh attempt.
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null, errorId: null });
    }
  }

  reset = (): void => {
    this.setState({ error: null, errorId: null });
  };

  override render(): ReactNode {
    const { error, errorId } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(error, this.reset);
    return <DefaultFallback error={error} errorId={errorId} onReset={this.reset} />;
  }
}

export function AppErrorBoundary({ children }: { children: ReactNode }) {
  return <BaseBoundary scope="app-root">{children}</BaseBoundary>;
}

export function RouteErrorBoundary({
  children, scope, resetKey,
}: { children: ReactNode; scope: string; resetKey?: string }) {
  return (
    <BaseBoundary
      scope={`route:${scope}`}
      {...(resetKey !== undefined ? { resetKey } : {})}
    >
      {children}
    </BaseBoundary>
  );
}

export function SectionErrorBoundary({
  children, scope, fallback,
}: {
  children: ReactNode;
  scope: string;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}) {
  // Wrap the default section fallback so useT() runs inside a real component
  // (not a plain function called from a class render). Custom `fallback` props
  // are passed through unchanged.
  const wrapped = fallback ?? ((error, reset) => (
    <DefaultSectionFallback error={error} reset={reset} />
  ));
  return (
    <BaseBoundary scope={`section:${scope}`} fallback={wrapped}>
      {children}
    </BaseBoundary>
  );
}

function DefaultSectionFallback({ error, reset }: { error: Error; reset: () => void }) {
  const t = useT();
  return (
    <div className="error-boundary-section">
      <AlertTriangle size={14} aria-hidden="true" />
      <span>{t('errorBoundary.inlinePrefix')}{error.message?.slice(0, 80)}</span>
      <button type="button" className="error-boundary-section-btn" onClick={reset}>
        {t('common.retry')}
      </button>
    </div>
  );
}

function DefaultFallback({
  error, errorId, onReset,
}: { error: Error; errorId: string | null; onReset: () => void }): ReactNode {
  const t = useT();
  // Only show stack in DEV — prod users get the friendly summary only.
  const showStack = import.meta.env.DEV;
  return (
    <div className="error-boundary-page">
      <div className="error-boundary-card">
        <AlertTriangle size={28} aria-hidden="true" className="error-boundary-icon" />
        <h1 className="error-boundary-title">{t('common.error')}</h1>
        <p className="error-boundary-summary">
          {t('errorBoundary.pageBody')}
        </p>
        <p className="error-boundary-detail">
          {error.message?.slice(0, 200) ?? '(no message)'}
        </p>
        {/* 19BL Session 2: the user-referenceable error handle. errorId may be
            null for one render frame (getDerivedStateFromError fires before
            componentDidCatch mints it) — guard so we never show an empty label. */}
        {errorId && (
          <p className="error-boundary-ref">{formatErrorRef(errorId)}</p>
        )}
        {showStack && error.stack && (
          <details className="error-boundary-stack">
            <summary>stack (DEV only)</summary>
            <pre>{error.stack}</pre>
          </details>
        )}
        <div className="error-boundary-actions">
          <button type="button" className="btn-primary" onClick={onReset}>
            <RotateCcw size={13} aria-hidden="true" /> {t('errorBoundary.retryPage')}
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => { window.location.reload(); }}
          >
            {t('errorBoundary.refreshApp')}
          </button>
          <Link to="/" className="btn-secondary" onClick={onReset}>
            <HomeIcon size={13} aria-hidden="true" /> {t('errorBoundary.home')}
          </Link>
        </div>
      </div>
    </div>
  );
}
